import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { getValidTxToken } from '../../helpers/integrations/BuildingEnginesPrism/token';
import {
  BEPrismTokenContextType,
  BEPrismTokenProviderProps,
  TokenResult,
} from './types';

const BEPrismTokenContext = createContext<BEPrismTokenContextType | undefined>(
  undefined
);

export function useBEPrismTokenContext(): BEPrismTokenContextType {
  const context = useContext(BEPrismTokenContext);

  if (!context) {
    throw new Error(
      'useBEPrismTokenContext must be used within a BEPrismTokenProvider'
    );
  }

  return context;
}

export function BEPrismTokenProvider({
  children,
  tokenRequest,
}: BEPrismTokenProviderProps) {
  const getTxToken = useCallback(async (): Promise<TokenResult> => {
    try {
      const token = await getValidTxToken(tokenRequest);

      return {
        isError: false,
        token,
      };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to get token';

      return {
        isError: true,
        token: null,
        errorMessage,
      };
    }
  }, [tokenRequest]);

  const value: BEPrismTokenContextType = useMemo(
    () => ({
      getTxToken,
    }),
    [getTxToken]
  );

  return (
    <BEPrismTokenContext.Provider value={value}>
      {children}
    </BEPrismTokenContext.Provider>
  );
}
